import { useEffect, useState } from 'react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { apiUrl } from '../../../apiBase';
import { ClipPlayer } from '../ClipPlayer';
import type { Clip } from '../assemblyClip';

// The sittings whose time has been sent (see TutorialDone).
const recorded = new Set<string>();

// A bare minute count for the assembly time ("18 min"); seconds under a minute.
function formatDuration(seconds: number, t: (k: string) => string): string {
  if (seconds < 60) return `${seconds} ${t('notice.public.seconds')}`;
  return `${Math.round(seconds / 60)} ${t('notice.public.minutes')}`;
}

// THE LAST SCREEN, after the last step: "Bravo !", the time it took, the finished product, and
// one button home (Louis 2026-10-23). It was two screens, the finished product with "Montage
// terminé" on it and then a congratulation card with a comparison to the average; the average is
// no longer shown, and the time is still sent, so the average goes on being counted.
//
// THE TIME: from when the tutorial was first opened in this tab (see PublicTutorial) to when
// this screen first appeared, frozen there.
export function TutorialDone({ docId, startedAt, finished, meshUrl, brightness, onHome }: {
  docId: string;
  startedAt: number | null;
  // The finished product, where there is one to draw.
  finished: Clip | null;
  meshUrl: (job: number) => string;
  brightness: number;
  onHome: () => void;
}) {
  const { t } = useLanguage();
  const [seconds] = useState(() => Math.round((Date.now() - (startedAt ?? Date.now())) / 1000));

  // Recorded ONCE per sitting, best-effort: the screen shows regardless. Once per sitting and
  // not per showing, so stepping back and on to this screen again is not a second assembly.
  useEffect(() => {
    const sitting = `${docId}:${startedAt}`;
    if (recorded.has(sitting)) return;
    recorded.add(sitting);
    fetch(apiUrl(`/api/notice/public/tutorial/${encodeURIComponent(docId)}/complete`), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ seconds }),
    }).catch(() => {});
  }, [docId, startedAt, seconds]);

  return (
    <div className="flex h-[calc(100dvh-5.5rem)] flex-col items-center">
      <h1 className="text-center font-sora text-5xl font-bold leading-tight text-notice-blue">
        {t('notice.public.congrats')}
      </h1>
      <div className="mt-3 text-center">
        <div className="text-sm font-bold uppercase tracking-wide text-notice-blue">
          {t('notice.public.timeLabel')}
        </div>
        <div className="mt-1 font-sora text-3xl font-bold text-notice-ink">{formatDuration(seconds, t)}</div>
      </div>
      {finished ? (
        <ClipPlayer
          clip={finished}
          label={t('notice.public.congrats')}
          meshUrl={meshUrl}
          className="mt-4 min-h-0 w-full flex-1 rounded-2xl border border-notice-blue/15 bg-notice-cream"
          brightness={brightness}
          autoPlay={false}
          still
          noBigView
        />
      ) : (
        <div className="flex-1" />
      )}
      <button
        type="button"
        onClick={onHome}
        className="mt-6 w-full max-w-md shrink-0 rounded-full bg-notice-ink py-4 text-base font-bold text-white shadow-sm transition-opacity hover:opacity-90"
      >
        {t('notice.public.home')}
      </button>
    </div>
  );
}
